import React, { useState, useEffect, useCallback } from 'react'
import { Link } from "react-router-dom";
import withRouter from "../../withRouter.js";
import { userid, currentTenant, thousandsdisplay, clientname, logo, currency } from '../../Common/Credentials.js';
import InventoryService from '../../inventory/InventoryService.js';
import moment from 'moment'
import { spin } from "../../Common/NewLoader.jsx";
import { inventoryid } from '../../Common/Permissions.js';
import { NumericFormat } from 'react-number-format';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable"
import Swal from 'sweetalert2';
import { TextField, MenuItem, Tooltip } from "@mui/material";

import EditIcon from "../../images/Icons/edit.png"
import ViewIcon from "../../images/Icons/preview.png";
import UpArrow from "../../images/Icons/up-arrow.png";

// Import CSS file
import '../../NewUI/NewUI.css';
import Breadcrumb from '../../NewUI/NewComponents/Breadcrumb.jsx';


const CostDataTable = ({ navigate }) => {
  const [cost, setCost] = useState([]);
  const [filteredCost, setFilteredCost] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list');
  const [selectedCost, setSelectedCost] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [hovered, setHovered] = useState(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [hoveredAction, setHoveredAction] = useState(null);
  const [isBackHovered, setIsBackHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filteredSidebarData, setFilteredSidebarData] = useState([]);
  const [hasScrolledToSelected, setHasScrolledToSelected] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
  const sidebarRef = React.useRef(null);
  const scrollTimeout = React.useRef(null);

  // Sort indicator component
  const SortIndicator = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fa fa-sort UI-sort-icon" />;
    }
    return sortConfig.direction === 'asc'
      ? <i className="fa fa-sort-up UI-sort-icon UI-sort-active" />
      : <i className="fa fa-sort-down UI-sort-icon UI-sort-active" />;
  };

  // Sorting function
  const sortData = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'name':
          aValue = a.name || '';
          bValue = b.name || '';
          break;
        case 'brand':
          aValue = a.brand || '';
          bValue = b.brand || '';
          break;
        case 'model':
          aValue = a.model || '';
          bValue = b.model || '';
          break;
        case 'rate':
          aValue = a.rate || 0;
          bValue = b.rate || 0;
          break;
        case 'date':
          aValue = a.date ? new Date(a.date).getTime() : 0;
          bValue = b.date ? new Date(b.date).getTime() : 0;
          break;
        default:
          aValue = a[sortConfig.key] || '';
          bValue = b[sortConfig.key] || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // NEW: Search highlighting methods
  const escapeRegex = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  const highlightSearchText = (text, searchTerm) => {
    if (!text || !searchTerm?.trim()) return text;

    const regex = new RegExp(`(${escapeRegex(searchTerm)})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      part.toLowerCase() === searchTerm.toLowerCase()
        ? (
          <mark
            key={index}
            className="UI-search-highlight"
          >
            {part}
          </mark>
        )
        : part
    );
  };

  // NEW: Format currency as string for highlighting
  const formatCurrencyAsString = (value) => {
    if (value === null || value === undefined) return '0';

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Handle resize
  const handleResize = () => {
    const mobile = window.innerWidth <= 768;
    setIsMobile(mobile);
    if (!mobile) {
      setShowSearch(false);
    }
  };

  const refreshCost = useCallback(() => {
    setLoading(true);
    InventoryService.retriveCost(currentTenant)
      .then(response => {
        const sortedCost = response.data.sort((a, b) => a.date < b.date ? 1 : -1);
        setCost(sortedCost);
        setFilteredCost(sortedCost);
        setLoading(false);
      })
      .catch((e) => {
        if (e.response?.data?.error === "Unauthorized") {
          Swal.fire({
            title: `Your Session is Lost: Please login Back to resume`,
            text: ".",
            icon: "error",
            confirmButtonText: "Ok",
            confirmButtonColor: "#152f5f",
          }).then(() => navigate('/'))
        }
        setLoading(false);
      });
  }, [navigate]);

  useEffect(() => {
    if (window.innerWidth <= 768) {
      setShowSearch(true);
    }
    window.addEventListener('resize', handleResize);

    if (!userid) {
      window.confirm('Your Session is Lost: Please login Back to resume');
      navigate('/');
      return;
    }
    setLoading(true);
    refreshCost();

    return () => {
      window.removeEventListener('resize', handleResize);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, [refreshCost, navigate]);

  useEffect(() => {
    // Auto-scroll in detail view (only once)
    if (currentView === 'detail' && selectedCost && sidebarRef.current && !hasScrolledToSelected) {
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }

      scrollTimeout.current = setTimeout(() => {
        const selectedElement = sidebarRef.current.querySelector(`[data-cost-id="${selectedCost.cid}"]`);
        if (selectedElement) {
          selectedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setHasScrolledToSelected(true);
        }
      }, 100);
    }
  }, [currentView, selectedCost, hasScrolledToSelected]);

  // Update filtered sidebar data when search term changes in detail view
  useEffect(() => {
    if (currentView === 'detail') {
      setFilteredSidebarData(getSortedData(filteredCost));
    }
  }, [searchTerm, currentView, filteredCost, sortConfig]);

  const checkCid = (cid) => {
    if (cid) {
      InventoryService.validateCid(cid)
        .then(response => {
          if (response.data === false) {
            navigate('/addcost', { state: { id: cid, pagename: "Edit Price" } });
          } else if (response.data === true) {
            Swal.fire({
              title: `Price cannot be altered as stock is added with this price`,
              text: ".",
              icon: "info",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            });
          }
        })
        .catch((e) => {
          if (e.response?.data?.error === "Unauthorized") {
            Swal.fire({
              title: `Your Session is Lost: Please login Back to resume`,
              text: ".",
              icon: "error",
              confirmButtonText: "Ok",
              confirmButtonColor: "#152f5f",
            }).then(() => navigate('/'));
          }
        });
    }
  };

  const pdf = useCallback(() => {
    const curdate = moment().format('DD-MM-YYYY HH:mm:ss');
    const columns = [
      { header: "Item Name", dataKey: 'name' },
      { header: "Brand", dataKey: 'brand' },
      { header: "Model", dataKey: 'model' },
      { header: "Date", dataKey: 'date' },
      { header: "Price", dataKey: 'rate' },
    ];

    const pdfdata = cost.map(e => ({
      ...e,
      date: moment(e.date).format('DD-MM-YYYY HH:mm')
    }));

    var doc = new jsPDF('p', 'pt', [842, 595]);
    autoTable(doc, {
      theme: 'grid',
      startY: 100,
      columnStyles: { 4: { halign: 'right' } },
      body: pdfdata,
      columns: columns,
      didDrawPage: (data) => {
        doc.setFontSize(16);
        doc.text("Price Details", 40, 50);
        doc.setFontSize(10);
        doc.text(`Generated on: ${curdate}`, 500, 50, { align: "right" });

        const pageCount = doc.internal.getNumberOfPages();
        doc.setFontSize(10);
        doc.text(`Page ${data.pageNumber} of ${pageCount}`, 500, 800, { align: "right" });
      },
    });

    doc.save(`PriceTable_${curdate}.pdf`);
  }, [cost]);

  const handleSearch = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredCost(cost);
    } else {
      const filtered = cost.filter(item =>
        item.name.toLowerCase().includes(term.toLowerCase()) ||
        item.brand.toLowerCase().includes(term.toLowerCase()) ||
        item.model.toLowerCase().includes(term.toLowerCase()) ||
        item.date.toLowerCase().includes(term.toLowerCase()) ||
        item.rate?.toString().includes(term)
      );
      setFilteredCost(filtered);
    }
  };

  // Get current page data for pagination
  const getCurrentPageData = () => {
    const sortedData = getSortedData(filteredCost);
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return sortedData.slice(indexOfFirstItem, indexOfLastItem);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleCostClick = (costItem) => {
    setSelectedCost(costItem);
    setCurrentView('detail');
    setHasScrolledToSelected(false);
    setFilteredSidebarData(getSortedData(filteredCost));
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedCost(null);
    setHasScrolledToSelected(false);
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '0';
    return (
      <NumericFormat
        displayType="text"
        value={value}
        decimalScale={2}
        fixedDecimalScale={true}
        thousandSeparator={true}
        thousandsGroupStyle={thousandsdisplay}
        prefix={currency === "INR" ? "₹" : currency === "USD" ? "$" : currency}
      />
    );
  };

  // Render pagination controls
  const renderPagination = () => {
    if (filteredCost.length <= itemsPerPage) return null;

    const totalPages = Math.ceil(filteredCost.length / itemsPerPage);

    return (
      <div className={`UI-pagination-container ${isMobile ? 'UI-pagination-container-mobile' : ''}`}>
        <div className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredCost.length)} of {filteredCost.length} prices
        </div>
        <div className={`UI-pagination-controls ${isMobile ? 'UI-pagination-controls-mobile' : ''}`}>
          <TextField
            select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            variant="outlined"
            size="small"
            className={`UI-page-size-select ${isMobile ? 'UI-page-size-select-mobile' : ''}`}
          >
            <MenuItem value={5}>5 per page</MenuItem>
            <MenuItem value={10}>10 per page</MenuItem>
            <MenuItem value={20}>20 per page</MenuItem>
            <MenuItem value={50}>50 per page</MenuItem>
          </TextField>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'first' && !(currentPage === 1) ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('first')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'First' : 'First'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === 1 ? 'UI-pagination-button-disabled' : ''} ${hovered === 'prev' && !(currentPage === 1) ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            onMouseEnter={() => !(currentPage === 1) && setHovered('prev')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Prev' : 'Previous'}
          </button>

          <span className={`UI-pagination-info ${isMobile ? 'UI-pagination-info-mobile' : ''}`}>
            Page {currentPage} of {totalPages}
          </span>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'next' && !(currentPage === totalPages) ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('next')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Next' : 'Next'}
          </button>

          <button
            className={`UI-pagination-button ${isMobile ? 'UI-pagination-button-mobile' : ''} ${currentPage === totalPages ? 'UI-pagination-button-disabled' : ''} ${hovered === 'last' && !(currentPage === totalPages) ? 'UI-pagination-button-hover' : ''}`}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            onMouseEnter={() => !(currentPage === totalPages) && setHovered('last')}
            onMouseLeave={() => setHovered(null)}
          >
            {isMobile ? 'Last' : 'Last'}
          </button>
        </div>
      </div>
    );
  };

  const renderTable = () => {
    const currentPageData = getCurrentPageData();

    if (filteredCost.length === 0) {
      return (
        <div className={`UI-no-data-message ${isMobile ? 'UI-no-data-message-mobile' : ''}`}>
          {searchTerm ? 'No prices found matching your search' : 'No prices available'}
        </div>
      );
    }

    return (
      <>
        <div className={`UI-table-container ${isMobile ? 'UI-table-container-mobile' : ''}`}>
          <table className={`UI-table ${isMobile ? 'UI-table-mobile' : ''}`}>
            <thead className="UI-table-header">
              <tr>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'name' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('name')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Item Name
                    <SortIndicator columnKey="name" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'brand' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('brand')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Brand
                    <SortIndicator columnKey="brand" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'model' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('model')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Model
                    <SortIndicator columnKey="model" />
                  </div>
                </th>
                <th
                  className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} ${sortConfig.key === 'rate' ? 'UI-th-sorted' : ''}`}
                  onClick={() => sortData('rate')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="UI-th-content">
                    Price
                    <SortIndicator columnKey="rate" />
                  </div>
                </th>
                {(inventoryid === "2" || inventoryid === "3") && (
                  <th className={`UI-th ${isMobile ? 'UI-th-mobile' : ''} UI-th-actions`}>Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentPageData.map((costItem) => (
                <tr
                  key={costItem.cid}
                  className={`UI-table-row ${isMobile ? 'UI-table-row-mobile' : ''} ${hoveredRow === costItem.cid ? 'UI-table-row-hovered' : ''} ${isMobile && hoveredRow === costItem.cid ? 'UI-table-row-hovered-mobile' : ''}`}
                  onClick={() => handleCostClick(costItem)}
                  onMouseEnter={() => setHoveredRow(costItem.cid)}
                  onMouseLeave={() => {
                    setHoveredRow(null);
                    setHoveredAction(null);
                  }}
                >
                  {/* Item Name Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''} UI-item-name ${hoveredRow === costItem.cid ? 'UI-item-name-hover' : ''}`}
                    style={{
                      color: hoveredRow === costItem.cid ? '#5f8f3f' : '',
                      transform: hoveredRow === costItem.cid ? 'scale(1.009)' : 'scale(1)',
                      transition: isMobile ? 'all 0.2s ease' : 'color 0.03s ease, transform 0.03s ease',
                    }}
                  >
                    <Tooltip
                      title="View Details"
                      placement="right"
                      arrow
                      open={!isMobile && hoveredRow === costItem.cid && !hoveredAction}
                    >
                      <span>{highlightSearchText(costItem.name, searchTerm)}</span>
                    </Tooltip>
                  </td>

                  {/* Brand Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''} ${hoveredRow === costItem.cid ? 'UI-brand-hover' : ''}`}
                    style={{
                      color: hoveredRow === costItem.cid ? '#5f8f3f' : '',
                    }}
                  >
                    {highlightSearchText(costItem.brand, searchTerm)}
                  </td>

                  {/* Model Cell */}
                  <td
                    className={`UI-td ${isMobile ? 'UI-td-mobile' : ''} ${hoveredRow === costItem.cid ? 'UI-brand-hover' : ''}`}
                    style={{
                      color: hoveredRow === costItem.cid ? '#5f8f3f' : '',
                    }}
                  >
                    {highlightSearchText(costItem.model, searchTerm)}
                  </td>

                  {/* Price Cell */}
                  <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''} UI-amount ${hoveredRow === costItem.cid ? 'UI-price-hover' : ''}`}>
                    {highlightSearchText(
                      formatCurrencyAsString(costItem.rate),
                      searchTerm
                    )}
                  </td>

                  {/* Actions Cell */}
                  {(inventoryid === "2" || inventoryid === "3") && (
                    <td className={`UI-td ${isMobile ? 'UI-td-mobile' : ''}`}>
                      <div className="UI-action-buttons">
                        {/* Edit Price */}
                        <Tooltip
                          title="Edit Price"
                          placement="left"
                          arrow
                          disableInteractive
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              checkCid(costItem.cid);
                            }}
                            className="UI-cost-edit-button-list"
                            onMouseEnter={() => setHoveredAction(`edit-${costItem.cid}`)}
                            onMouseLeave={() => setHoveredAction(null)}
                          >
                            <img
                              src={EditIcon}
                              alt="Edit"
                              className={`UI-cost-edit-icon-list ${hoveredAction === `edit-${costItem.cid}`
                                ? 'UI-cost-edit-icon-list-hover'
                                : ''
                                }`}
                            />
                          </button>
                        </Tooltip>

                        {/* Add Stock */}
                        <span
                          style={{ display: "inline-flex" }}
                          onMouseEnter={() => setHoveredAction(`stock-${costItem.cid}`)}
                          onMouseLeave={() => setHoveredAction(null)}
                        >
                          <Link
                            to="/addstock"
                            state={{
                              id: -1,
                              pagename: "Add Stock",
                              items: {
                                id: costItem.id,
                                name: costItem.name,
                                brand: costItem.brand,
                                model: costItem.model,
                                uomname: costItem.uomname,
                                uom: costItem.uomid
                              },
                              preselectedCid: costItem.cid
                            }}
                            className={`UI-cost-add-stock-button ${isMobile ? 'UI-add-action-button-mobile' : ''
                              } ${hoveredAction === `stock-${costItem.cid}`
                                ? 'UI-cost-add-stock-button-hover'
                                : ''
                              }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fa fa-plus" style={{ fontSize: "0.7rem", marginRight: "4px" }} />
                            {!isMobile && "Add Stock"}
                          </Link>
                        </span>
                      </div>

                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </>
    );
  };

  const renderListView = () => {
    return renderTable();
  };

  const renderDetailView = () => {
    if (!selectedCost) return null;

    return (
      <div className="UI-detail-view">
        {/* Sidebar with cost list */}
        <div className="UI-sidebar">
          <div className="UI-sidebar-header">
            <button
              className={`UI-back-button ${isBackHovered ? 'UI-back-button-hover' : ''}`}
              onClick={handleBackToList}
              onMouseEnter={() => setIsBackHovered(true)}
              onMouseLeave={() => setIsBackHovered(false)}
            >
              <i className="fa fa-arrow-left" /> Back
            </button>
          </div>

          <div className="UI-sidebar-list" ref={sidebarRef}>
            {filteredSidebarData.map((costItem) => (
              <div
                key={costItem.cid}
                data-cost-id={costItem.cid}
                className={`UI-sidebar-item ${selectedCost.cid === costItem.cid ? 'UI-sidebar-item-active' : ''} ${hovered === costItem.cid ? 'UI-sidebar-item-hover' : ''}`}
                onClick={() => setSelectedCost(costItem)}
                onMouseEnter={() => setHovered(costItem.cid)}
                onMouseLeave={() => setHovered(null)}
              >
                <div className={`UI-sidebar-item-name ${hovered === costItem.cid ? 'UI-sidebar-item-name-hover' : ''}`}>
                  {highlightSearchText(costItem.name, searchTerm)}
                </div>
                <div className={`UI-sidebar-item-code ${hovered === costItem.cid ? 'UI-sidebar-item-code-hover' : ''}`}>
                  {highlightSearchText(costItem.brand, searchTerm)} {highlightSearchText(costItem.model, searchTerm)}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail Content */}
        <div className="UI-detail-content">
          <div className="UI-detail-header">
            <div className="UI-detail-title-wrapper">
              <h2 className="UI-detail-title">{highlightSearchText(selectedCost.name, searchTerm)}</h2>
              <p className="UI-detail-subtitle">
                Price ID: {selectedCost.cid}
              </p>
            </div>

            <div className="UI-detail-actions">
              {/* Edit Price */}
              <Tooltip
                title="Edit Price"
                placement="bottom"
                arrow
                disableInteractive
              >
                <button
                  onClick={() => checkCid(selectedCost.cid)}
                  className={`UI-detail-action-button UI-cost-detail-edit-button ${hovered === "editPrice" ? 'UI-edit-button-hover' : ''
                    }`}
                  onMouseEnter={() => setHovered("editPrice")}
                  onMouseLeave={() => setHovered(null)}
                >
                  <img
                    src={EditIcon}
                    alt="Edit"
                    className={`UI-action-icon ${hovered === "editPrice" ? 'UI-action-icon-hover' : ''
                      } ${isMobile ? 'UI-action-icon-mobile' : ''}`}
                  />
                </button>
              </Tooltip>

              {/* Add Stock */}

              <span
                onMouseEnter={() => setHovered("addStock")}
                onMouseLeave={() => setHovered(null)}
                style={{ display: "inline-flex" }}
              >
                <Link
                  to="/addstock"
                  state={{
                    id: -1,
                    pagename: "Add Stock",
                    items: {
                      id: selectedCost.id,
                      name: selectedCost.name,
                      brand: selectedCost.brand,
                      model: selectedCost.model,
                      uomname: selectedCost.uomname,
                      uom: selectedCost.uomid
                    },
                    preselectedCid: selectedCost.cid
                  }}
                  className={`UI-detail-action-button UI-cost-detail-addstock-button ${hovered === "addStock"
                    ? 'UI-cost-detail-addstock-button-hover'
                    : ''
                    }`}
                >
                  <i className="fa fa-plus" style={{ fontSize: '0.7rem' }} />
                  {!isMobile && "Add Stock"}
                </Link>
              </span>

            </div>

          </div>

          <div className="UI-detail-grid-two-column">
            {/* Basic Information */}
            <div className="UI-detail-section">
              <h3 className="UI-section-title">Basic Information</h3>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Item Name</span>
                <span className="UI-detail-value">{highlightSearchText(selectedCost.name, searchTerm)}</span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Brand</span>
                <span className="UI-detail-value">{highlightSearchText(selectedCost.brand, searchTerm)}</span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Model/Grade</span>
                <span className="UI-detail-value">{highlightSearchText(selectedCost.model, searchTerm)}</span>
              </div>
            </div>

            {/* Price Information */}
            <div className="UI-detail-section">
              <h3 className="UI-section-title">Price Information</h3>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Price</span>
                <span className="UI-detail-value">
                  {highlightSearchText(
                    formatCurrencyAsString(selectedCost.rate),
                    searchTerm
                  )}
                </span>
              </div>
              <div className="UI-detail-row">
                <span className="UI-detail-label">Last Updated</span>
                <span className="UI-detail-value">
                  {highlightSearchText(moment(selectedCost.date).format('DD-MM-YYYY HH:mm'), searchTerm)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`UI-container ${isMobile ? 'UI-container-mobile' : ''}`}>
      <div className="UI-card">
        {/* Header */}
        <div className={`UI-header ${isMobile ? 'UI-header-mobile' : ''}`}>
        <Breadcrumb breadcrumbs={[{name:"Home",path:"/inventory"},{name:"Item Price",path:null}]}/>

          <div className={`UI-header-content ${isMobile ? 'UI-header-content-mobile' : ''}`}>
            <div>
              <h1 className={`UI-title ${isMobile ? 'UI-title-mobile' : ''}`}>
                Price Details
              </h1>
              <p className={`UI-subtitle ${isMobile ? 'UI-subtitle-mobile' : ''}`}>
                {currentView === 'detail' && selectedCost
                  ? `ID: ${selectedCost.cid}`
                  : 'Manage product pricing information'
                }
              </p>
            </div>

            <div className={`UI-header-actions ${isMobile ? 'UI-header-actions-mobile' : ''}`}>
              {/* Mobile Search */}
              {isMobile && (
                <input
                  type="text"
                  placeholder="Search prices..."
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="UI-mobile-search-input"
                />
              )}

              <div className={`UI-buttons-row ${isMobile ? 'UI-buttons-row-mobile' : ''}`}>
                {/* Desktop Search Toggle */}
                {!isMobile && (
                  <div
                    className={`UI-search-container ${showSearch ? 'UI-search-container-expanded' : ''} ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                    onClick={() => {
                      if (!showSearch) setShowSearch(true);
                    }}
                    onMouseEnter={() => setHovered("search")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i
                      className={`fa fa-search UI-search-icon ${hovered === "search" ? 'UI-search-container-hover' : ''}`}
                    />
                    {!showSearch && "Search"}
                    {showSearch && (
                      <input
                        type="text"
                        placeholder="Search prices..."
                        value={searchTerm}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="UI-search-input"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                        onBlur={() => {
                          if (searchTerm.trim() === "") {
                            setShowSearch(false);
                          }
                        }}
                      />
                    )}
                  </div>
                )}

                {/* Export button */}
                <button
                  className={`UI-btn ${isMobile ? 'UI-btn-mobile' : ''} UI-btn-outline ${hovered === "export" ? 'UI-btn-outline-hover' : ''}`}
                  onClick={pdf}
                  onMouseEnter={() => setHovered("export")}
                  onMouseLeave={() => setHovered(null)}
                >
                  <i className="fa fa-file-pdf" /> {isMobile ? "Export" : "Export"}
                </button>

                {/* New Price button */}
                {(inventoryid === "2" || inventoryid === "3") && (
                  <Link
                    to="/addcost"
                    state={{ id: -1, pagename: "Add Price" }}
                    className={`UI-btn ${isMobile ? 'UI-btn-mobile' : ''} UI-btn-primary ${hovered === "new" ? 'UI-btn-primary-hover' : ''}`}
                    onMouseEnter={() => setHovered("new")}
                    onMouseLeave={() => setHovered(null)}
                  >
                    <i className="fa fa-plus" /> {isMobile ? "New" : "New Price"}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="UI-loading-container">
            {spin}
          </div>
        ) : (
          <>
            {/* Switch between list and detail view */}
            {currentView === 'list'
              ? renderListView()
              : renderDetailView()
            }
          </>
        )}
      </div>
    </div>
  );
};

export default withRouter(CostDataTable);